import { useState } from 'react';
import { createAccount, recoverAccount } from '../api/checkin';

interface LoginFormProps {
  onLogin: (data: { id: string; name: string; recoveryCode: string }) => void;
}

const LoginForm = ({ onLogin }: LoginFormProps) => {
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [name, setName] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdCode, setCreatedCode] = useState('');
  const [hasConfirmedSave, setHasConfirmedSave] = useState(false);
  const [newEmployeeData, setNewEmployeeData] = useState<{
    id: string;
    name: string;
    recoveryCode: string;
  } | null>(null);

  const handleCreateAccount = async () => {
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await createAccount(name);
      setCreatedCode(result.recoveryCode);
      
      // Store employee data for later use
      const employeeData = {
        id: result.employee.id,
        name: result.employee.name,
        recoveryCode: result.recoveryCode,
      };

      setNewEmployeeData(employeeData);
      // Don't call onLogin here - wait for user confirmation
    } catch (error) {
      setError(`Error creating account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverAccount = async () => {
    if (recoveryCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const employee = await recoverAccount(recoveryCode);
      
      // Store employee data
      const employeeData = {
        id: employee.id,
        name: employee.name,
        recoveryCode: employee.recovery_code,
      };

      // Save to localStorage
      localStorage.setItem('employeeData', JSON.stringify(employeeData));
      onLogin(employeeData);
    } catch (error) {
      setError(`Error recovering account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (createdCode) {
    return (
      <div className="max-w-md mx-auto">
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="text-green-500 text-xl font-semibold mb-2">
            🎉 Account Created Successfully!
          </div>
          <div className="text-gray-600 text-sm">
            Your account has been created. Please save your recovery code immediately.
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-amber-400 text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Important!</h3>
              <div className="mt-1 text-sm text-amber-700">
                This recovery code will only be shown once. You will need it to access your account if you lose your login information.
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Code Display */}
        <div className="mb-6">
          <label className="block mb-3 font-semibold text-gray-800">
            Your Recovery Code
          </label>
          <div className="relative">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl text-center shadow-inner">
              <div className="text-3xl font-mono font-bold text-blue-900 tracking-wider select-all mb-2">
                {createdCode}
              </div>
              <div className="text-xs text-blue-600 uppercase tracking-wide">
                Recovery Code
              </div>
            </div>
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full transform rotate-12">
              SAVE THIS!
            </div>
          </div>
        </div>

        {/* Screenshot Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
            <span className="mr-2">📱</span>
            How to Save Your Recovery Code:
          </h4>
          <ol className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <span className="inline-block w-5 h-5 bg-blue-500 text-white text-xs rounded-full text-center leading-5 mr-2 mt-0.5 flex-shrink-0">1</span>
              <span><strong>Take a screenshot</strong> of this entire screen</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-5 h-5 bg-blue-500 text-white text-xs rounded-full text-center leading-5 mr-2 mt-0.5 flex-shrink-0">2</span>
              <span><strong>Save the screenshot</strong> to a secure location</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-5 h-5 bg-blue-500 text-white text-xs rounded-full text-center leading-5 mr-2 mt-0.5 flex-shrink-0">3</span>
              <span><strong>Write down the code</strong> as a backup (optional)</span>
            </li>
          </ol>
        </div>

        {/* Copy Button */}
        <button
          onClick={() => {
            navigator.clipboard.writeText(createdCode).then(() => {
              alert('Recovery code copied to clipboard!');
            }).catch(() => {
              alert('Could not copy to clipboard. Please write down the code manually.');
            });
          }}
          className="w-full mb-4 py-2 px-4 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors text-sm"
        >
          📋 Copy Recovery Code to Clipboard
        </button>

        {/* Confirmation Checkbox */}
        <div className="mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={hasConfirmedSave}
              onChange={(e) => setHasConfirmedSave(e.target.checked)}
              className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              <strong>I have saved my recovery code</strong> by taking a screenshot or writing it down. 
              I understand that I will need this code to recover my account if I lose access.
            </span>
          </label>
        </div>

        {/* Continue Button */}
        <button 
          onClick={() => {
            if (newEmployeeData && hasConfirmedSave) {
              // Save to localStorage
              localStorage.setItem('employeeData', JSON.stringify(newEmployeeData));
              onLogin(newEmployeeData);
            }
          }}
          disabled={!hasConfirmedSave}
          className={`w-full py-3 text-base font-medium rounded-lg transition-colors ${
            hasConfirmedSave 
              ? 'bg-blue-500 text-white hover:bg-blue-600' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue to Check-in System
        </button>

        {!hasConfirmedSave && (
          <p className="text-xs text-gray-500 text-center mt-2">
            Please confirm that you have saved your recovery code to continue
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      {isCreateMode ? (
        <div>
          <div className="mb-6">
            <label htmlFor="name" className="block mb-2 font-medium text-gray-700">Enter Your Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full Name"
              className="w-full p-3 border border-gray-200 rounded text-base transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <button
            onClick={handleCreateAccount}
            disabled={loading}
            className="w-full py-3 text-base bg-blue-500 text-white font-medium rounded hover:bg-blue-600 transition-colors disabled:bg-gray-400"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
          <div className="mt-4 text-center">
            Already have an account?{' '}
            <button
              onClick={() => setIsCreateMode(false)}
              className="text-blue-500 cursor-pointer p-2 inline-block"
            >
              Recover Account
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-6">
            <label htmlFor="recovery-code" className="block mb-2 font-medium text-gray-700">
              Enter 6-Digit Recovery Code
            </label>
            <input
              type="text"
              id="recovery-code"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder="6-Digit Code"
              maxLength={6}
              className="w-full p-3 border border-gray-200 rounded text-base transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <button
            onClick={handleRecoverAccount}
            disabled={loading}
            className="w-full py-3 text-base bg-blue-500 text-white font-medium rounded hover:bg-blue-600 transition-colors disabled:bg-gray-400"
          >
            {loading ? 'Recovering account...' : 'Recover Account'}
          </button>
          <div className="mt-4 text-center">
            Don't have an account?{' '}
            <button
              onClick={() => setIsCreateMode(true)}
              className="text-blue-500 cursor-pointer p-2 inline-block"
            >
              Create Account
            </button>
          </div>
        </div>
      )}
      {error && <div className="text-red-500 font-medium mt-4">{error}</div>}
    </div>
  );
};

export default LoginForm; 